/**
 * Service-worker registration shim. Wraps `virtual:pwa-register` (provided
 * by `vite-plugin-pwa`) and exposes a tiny event API so the UpdateToast
 * component (and anyone else who cares) can react to:
 *
 *   - `needRefresh` — a new SW has installed in the background, waiting
 *      for an explicit reload. Fired after `installing → installed → waiting`.
 *      Calling `applyUpdate()` swaps to the new SW and reloads.
 *
 *   - `offlineReady` — first install completed. Useful to flash a "ready
 *      for offline use" toast on the first run.
 *
 * Why manual registration (instead of `injectRegister: 'auto'`):
 *   - We want the SW prompt UX wired into our component tree (UpdateToast)
 *     instead of a generic browser banner.
 *   - We can defer SW registration until after first paint so the SW install
 *     doesn't compete with critical JS / HTTP requests.
 *   - We can short-circuit registration during local dev (vite dev server)
 *     so HMR keeps working without SW interference.
 *
 * NB: `virtual:pwa-register` is a Vite virtual module. Its types live in
 * `vite-plugin-pwa/client.d.ts`; we add the triple-slash directive in
 * `vite-env.d.ts` so `tsc -b` accepts the import.
 */
import { registerSW } from 'virtual:pwa-register';

export type PwaEvent =
  | { type: 'needRefresh' }
  | { type: 'offlineReady' }
  | { type: 'registered' }
  | { type: 'error'; error: unknown };

type Listener = (ev: PwaEvent) => void;

let updateSW: ((reload?: boolean) => Promise<void>) | null = null;
const listeners = new Set<Listener>();

/** Reference to the live SW registration so visibility / online handlers
 *  can trigger an `update()` outside of the browser's default ≈24h cadence.
 *  The browser only fires `updatefound` on navigation or its own slow
 *  internal poll, so without a manual ping the user has to hard-reload
 *  once before they ever see the "新版本可用" toast. */
let swRegistration: ServiceWorkerRegistration | null = null;

function emit(ev: PwaEvent): void {
  for (const fn of listeners) {
    try {
      fn(ev);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[pwa] listener threw:', err);
    }
  }
}

/**
 * Subscribe to PWA lifecycle events. Returns an unsubscribe function.
 * Call this BEFORE {@link initPwa} or you'll miss the early events.
 */
export function subscribePwa(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Trigger the SW skipWaiting + reload sequence. No-op if no waiting SW.
 */
export async function applyPwaUpdate(): Promise<void> {
  if (!updateSW) return;
  try {
    await updateSW(true);
  } catch (err) {
    emit({ type: 'error', error: err });
  }
}

/**
 * Idempotent. Call once at app boot. Skips registration in:
 *   - non-secure contexts other than localhost (SW spec requires HTTPS or
 *     loopback; the Vite dev server on 127.0.0.1 counts as secure).
 *   - environments lacking `navigator.serviceWorker` (Firefox private mode,
 *     ancient browsers).
 *   - Vite dev mode unless `ADH_PWA_DEV=1` (set in env, see vite.config.ts).
 */
export function initPwa(): void {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;
  if (updateSW) return; // already registered

  updateSW = registerSW({
    immediate: false,
    onNeedRefresh() {
      emit({ type: 'needRefresh' });
    },
    onOfflineReady() {
      emit({ type: 'offlineReady' });
    },
    onRegisteredSW(_swUrl, registration) {
      emit({ type: 'registered' });
      // Hold onto the registration so the visibility / online watchers
      // below can poke it. `registration` may be undefined on browsers
      // that refused to install the SW (file://, private mode, etc) —
      // guard so we don't `.update()` on null.
      if (registration) swRegistration = registration;
    },
    onRegisterError(err) {
      emit({ type: 'error', error: err });
    },
  });

  // Foreground → trigger an immediate update check. Users coming back
  // to a long-lived tab see "新版本可用" within a second or two without
  // having to hard-reload. We deliberately do NOT keep a polling
  // interval running — every poke is event-driven.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') triggerSwUpdate();
  });
  // Network restored → first chance the SW has to fetch /sw.js anyway.
  window.addEventListener('online', triggerSwUpdate);
}

function triggerSwUpdate(): void {
  if (!swRegistration) return;
  // `.update()` is a no-op if no newer SW is available; on success the
  // SW transitions to `installing → installed → waiting` and we get
  // `onNeedRefresh` automatically. Fire-and-forget.
  swRegistration.update().catch((err) => {
    // Don't spam listeners with `error` events on every transient
    // network blip; downgrade to a console warning.
    // eslint-disable-next-line no-console
    console.warn('[pwa] update() failed:', err);
  });
}
